import { BUG_SPRITE_DATA_URL, BUG_SPRITE_TILE } from './bugSprite';
import { BUG_VARIANTS, type BugVariant } from './bugVariants';

/**
 * The bug sprite, decoded once and drawn synchronously (TOADO-296).
 *
 * `drawPin` is synchronous and called from a paint loop, from a service
 * worker's `OffscreenCanvas`, and from a flatten that runs to completion in
 * one turn. None of those can await an image, and making them able to would
 * mean making `drawShape` async and pushing that through every caller. So the
 * decode happens once, off to the side, and a pin drawn before it finishes
 * uses the hand-drawn glyph rather than an empty disc.
 *
 * Decoded from bytes rather than fetched. `createImageBitmap` is the one
 * decode path that exists in every context the marker is drawn in: the
 * extension's capture-time rasterizer runs in a service worker, which has no
 * `Image` at all. Building the blob from `atob` rather than `fetch(dataUrl)`
 * keeps a content script's request out of the picture entirely, since that is
 * a surface with its own rules on someone else's page.
 */

let sprite: ImageBitmap | null = null;
let loading: Promise<void> | null = null;
/** Surfaces that have already drawn a fallback and want to draw it again. */
const waiting = new Set<() => void>();

/**
 * Start decoding, if it has not started. Safe to call repeatedly.
 *
 * Exported so a surface that knows it is about to draw pins can warm it on
 * mount, which is the difference between the first bug pin on a page being a
 * hand-drawn glyph and it being the real one.
 */
export function loadBugSprite(): Promise<void> {
  if (loading) return loading;
  loading = decode().then(
    (bitmap) => {
      sprite = bitmap;
      // Anything already on screen drew the fallback. Told, rather than left
      // to find out on the next interaction: a canvas that repaints only when
      // a shape changes would keep showing the generic bug until somebody
      // clicked something, which on a ticket nobody is editing is forever.
      for (const notify of [...waiting]) {
        waiting.delete(notify);
        try {
          notify();
        } catch {
          // A repaint that throws is that surface's problem, not the next
          // surface's: one broken listener must not swallow the rest.
        }
      }
    },
    () => {
      // No sprite, and no error to raise: every caller already draws the
      // fallback when there is none. jsdom reaches this on every run, because
      // it implements neither `createImageBitmap` nor image decoding.
    },
  );
  return loading;
}

/**
 * Where this variant's tile is in the sprite, if it can be drawn at all.
 *
 * Null covers three different things that all mean the same to a caller: the
 * sprite has not decoded yet, it never will in this environment, or the
 * variant is one this build does not know. The last of those is the one that
 * matters most, because a newer client can write a variant that this one has
 * never heard of and the pin still has to appear.
 */
export function bugSpriteTile(variant: unknown): { image: ImageBitmap; sx: number } | null {
  if (!sprite) {
    void loadBugSprite();
    return null;
  }
  const index = BUG_VARIANTS.indexOf(variant as BugVariant);
  if (index < 0) return null;
  return { image: sprite, sx: index * BUG_SPRITE_TILE };
}

async function decode(): Promise<ImageBitmap> {
  if (typeof createImageBitmap !== 'function' || typeof atob !== 'function') {
    throw new Error('This environment cannot decode the bug sprite.');
  }
  const base64 = BUG_SPRITE_DATA_URL.slice(BUG_SPRITE_DATA_URL.indexOf(',') + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return await createImageBitmap(new Blob([bytes], { type: 'image/png' }));
}

/**
 * Repaint when the sprite arrives, if it is not here yet.
 *
 * Returns a function to stop waiting, because the surface that asked can be
 * unmounted before the decode finishes and a repaint of a disposed canvas is
 * at best wasted and at worst a throw from inside someone else's teardown.
 * Called once and then forgotten: a decode happens once per document.
 */
export function onBugSpriteReady(repaint: () => void): () => void {
  if (sprite) return () => undefined;
  waiting.add(repaint);
  void loadBugSprite();
  return () => waiting.delete(repaint);
}

/** Is the sprite decoded and ready to draw? */
export function bugSpriteReady(): boolean {
  return sprite !== null;
}

/**
 * Supply the sprite already decoded, for a context with no
 * `createImageBitmap`: the server, which draws a ticket's pins into the
 * pictures it sends to Jira (TOADO-716) with a Node canvas's own image.
 */
export function provideBugSprite(image: ImageBitmap): void {
  sprite = image;
  loading = Promise.resolve();
}

/** For tests: forget the decoded sprite so the fallback path can be driven. */
export function resetBugSpriteForTests(): void {
  sprite = null;
  loading = null;
  waiting.clear();
}

/**
 * For tests: supply a sprite without decoding one.
 *
 * Node has no image decoder, so the drawing and the decoding have to be
 * testable apart. This is the drawing half.
 */
export function setBugSpriteForTests(image: ImageBitmap): void {
  sprite = image;
  loading = Promise.resolve();
}
